import React from 'react';
import { Section, SectionHeader } from '../../design-system';
import FadeIn from '../components/FadeIn';
import ProofStatsBar from '../components/ProofStatsBar';
import ProofPhotoWall from '../components/ProofPhotoWall';
import AdvertiserInquiryForm from '../../Pages/Landing/AdvertiserInquiryForm';
import { useNetworkStats } from '../hooks/useNetworkStats';
import { ADVERTISER_LOGOS } from '../data/proof';
import AdvertiserLandingLayout, { scrollToSection } from './AdvertiserLandingLayout';
import advertiserImage from '../../assets/images/cover2.png';

const INQUIRY_SOURCE = 'advertiser-landing';
const DEFAULT_STARTING_PRICE = 59;

const TRUST_STRIP = [
  { icon: '⌖', label: 'Hyper-local reach' },
  { icon: '◉', label: 'High-attention screens' },
  { icon: '◧', label: 'Full-screen, unskippable' },
  { icon: '◷', label: 'Simple monthly packages' },
];

const WHAT_YOU_GET = [
  {
    icon: '▦',
    title: 'Screens inside real businesses',
    description: 'Your ad runs on TVs and storefront screens in restaurants and local shops people visit every week.',
  },
  {
    icon: '⌖',
    title: 'A local, in-person audience',
    description: 'Reach customers in the neighborhoods you actually serve — not random clicks from across the country.',
  },
  {
    icon: '◧',
    title: 'Full-screen, unmissable placement',
    description: 'No tiny banners and nothing to scroll past. Your ad owns the whole screen.',
  },
  {
    icon: '✦',
    title: 'Done-for-you setup',
    description: 'We help you pick screens, prep your ad, and get live. No software to learn.',
  },
  {
    icon: '⊕',
    title: 'Optional location targeting',
    description: 'Want to be near a venue, a busy street, or a specific area? Tell us and we will place you there.',
  },
  {
    icon: '◷',
    title: 'Simple monthly packages',
    description: 'Clear, predictable pricing. Start with one screen and add more whenever you are ready.',
  },
];

const ALTERNATIVES = [
  {
    name: 'Social media ads',
    limitation: 'Scrolled past in a split second, blocked, or skipped. You are renting attention you never really get.',
  },
  {
    name: 'Flyers & mailers',
    limitation: 'Tossed in seconds, impossible to track, and most never get read at all.',
  },
  {
    name: 'Billboards',
    limitation: 'Expensive, locked into long contracts, and glanced at for two seconds at 45 mph.',
  },
];

const INVERT_ADVANTAGES = [
  'Seen by people already seated, waiting, or browsing',
  'Full-screen and impossible to scroll past',
  'Plays on repeat in the neighborhoods you choose',
  'Starts for about the price of a few coffees a month',
];

const IDEAL_FOR = [
  'Restaurants',
  'Gyms & studios',
  'Salons & barbershops',
  'Real estate',
  'Auto shops',
  'Med spas',
  'Dental & medical',
  'Local services',
];

const STEPS = [
  {
    number: '01',
    title: 'Reach out',
    description: 'Tell us your goal and budget. Takes two minutes — no account needed.',
  },
  {
    number: '02',
    title: 'Pick your spots',
    description: 'We recommend screens, locations, and a package that fits what you want to spend.',
  },
  {
    number: '03',
    title: 'Send your ad',
    description: 'Already have creative? Send it over. Need one? We will help you put it together.',
  },
  {
    number: '04',
    title: 'Go live',
    description: 'Your ad starts rotating on real screens across the neighborhood.',
  },
];

const PRICING_INCLUDES = [
  'One month of placement per screen',
  'Full-screen video or image play',
  'Rotation throughout business hours',
  'Help getting your ad screen-ready',
];

const FAQS = [
  {
    q: 'Who actually sees these ads?',
    a: 'Real people inside local businesses — customers seated at restaurants, people waiting in line, and shoppers browsing storefronts. They are paying attention, not scrolling past.',
  },
  {
    q: 'How long does setup take?',
    a: 'Most campaigns can go live within a few days. Send us your ad and approve a plan, and we handle the rest. Tell us your timeline and we will work to hit it.',
  },
  {
    q: 'Can I advertise at specific locations?',
    a: 'Yes. Want to be in a certain neighborhood, near a specific venue, or on a busy street? Let us know and we will place you on the screens that fit.',
  },
  {
    q: 'Do I need a finished ad?',
    a: 'No. If you already have a video or image, great. If not, mention it when you reach out and our team can help you create one.',
  },
  {
    q: 'Can I update my ad later?',
    a: 'Yes. You can refresh your creative for new promotions, seasons, or offers. Just send us the new ad.',
  },
  {
    q: 'Is there a long contract?',
    a: 'No long-term lock-in. Packages run monthly so you can start small, see how it performs, and scale up when you are ready.',
  },
];

const filledLogos = ADVERTISER_LOGOS.filter((a) => a.logo || a.label);

function CtaButton({ children, variant = 'primary', target = 'inquiry', className = '' }) {
  const base =
    'inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-semibold transition-colors';
  const styles =
    variant === 'primary'
      ? 'text-white bg-invert-orange hover:bg-invert-orange-deep shadow-lg shadow-invert-orange/25'
      : 'text-white bg-white/10 border border-white/20 hover:bg-white/20 backdrop-blur-sm';
  return (
    <button type="button" onClick={() => scrollToSection(target)} className={`${base} ${styles} ${className}`}>
      {children}
    </button>
  );
}

function AdvertiserLogoTile({ advertiser }) {
  return (
    <div className="flex items-center justify-center h-40 sm:h-44 px-8 rounded-2xl border border-invert-border bg-white hover:shadow-md transition-shadow">
      {advertiser.logo ? (
        <img
          src={advertiser.logo}
          alt={advertiser.label}
          loading="lazy"
          className="max-h-24 sm:max-h-28 w-auto object-contain"
        />
      ) : (
        <span className="text-base font-semibold text-slate-600">{advertiser.label}</span>
      )}
    </div>
  );
}

export default function AdvertiserLanding() {
  const { stats } = useNetworkStats();
  const startingPrice = stats?.pricingFromUsd || DEFAULT_STARTING_PRICE;

  return (
    <AdvertiserLandingLayout>
      {/* Hero */}
      <section className="relative bg-invert-navy text-white overflow-hidden pt-28 lg:pt-36 pb-20 lg:pb-28">
        <div className="absolute inset-0 bg-glow-orange pointer-events-none" />
        <div className="absolute inset-0 bg-hero-gradient opacity-90" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
                Local Advertising That Gets Seen
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
                Turn Attention Into{' '}
                <span className="text-gradient-orange">Paying Customers</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                INVERT runs your ad full-screen on TVs inside restaurants and local businesses — and on storefront
                screens — where people are sitting, waiting, and browsing. Real attention in your own neighborhood.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <CtaButton variant="primary" target="pricing">Get Pricing</CtaButton>
                <CtaButton variant="secondary" target="inquiry">Book a Call</CtaButton>
              </div>
              <p className="text-sm text-slate-400 mt-6">
                No account required · Starts at ${startingPrice}/mo per screen · Reply within one business day
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                src={advertiserImage}
                alt="An advertiser campaign playing on the INVERT network"
                className="rounded-2xl shadow-xl"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <div className="bg-invert-navy-muted border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUST_STRIP.map((item) => (
              <div key={item.label} className="flex items-center gap-3 justify-center lg:justify-start">
                <span className="text-xl text-invert-orange shrink-0">{item.icon}</span>
                <span className="text-sm font-medium text-slate-200">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What you get */}
      <Section id="what-you-get">
        <SectionHeader
          label="What You Get"
          title="Advertise on screens inside real businesses"
          subtitle="Everything you need to reach local customers in person — without the hassle of running it yourself."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHAT_YOU_GET.map((item, i) => (
            <FadeIn key={item.title} delay={i * 0.06}>
              <div className="rounded-2xl border border-invert-border bg-white p-6 h-full hover:shadow-md hover:border-invert-orange/30 transition-all duration-300">
                <span className="text-2xl text-invert-orange mb-4 block">{item.icon}</span>
                <h3 className="text-lg font-semibold text-invert-navy mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Why INVERT works (vs alternatives) */}
      <Section id="why" className="bg-slate-50">
        <SectionHeader
          label="Why It Works"
          title="Reach people where they're actually paying attention"
          subtitle="Most ads fight for a sliver of attention online. INVERT puts you in the real world, in front of people who are sitting, waiting, or browsing."
        />
        <div className="grid lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <FadeIn>
            <div className="rounded-2xl border border-invert-border bg-white p-7 h-full">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-5">
                The usual options
              </p>
              <ul className="space-y-5">
                {ALTERNATIVES.map((alt) => (
                  <li key={alt.name}>
                    <p className="flex items-start gap-2 text-invert-navy font-semibold">
                      <span className="text-slate-300 mt-0.5">✕</span>
                      {alt.name}
                    </p>
                    <p className="text-sm text-slate-500 leading-relaxed ml-6">{alt.limitation}</p>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="rounded-2xl border-2 border-invert-orange/40 bg-white p-7 h-full shadow-md">
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-5">
                With INVERT
              </p>
              <ul className="space-y-5">
                {INVERT_ADVANTAGES.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-invert-orange/15 text-invert-orange text-sm shrink-0 mt-0.5">
                      ✓
                    </span>
                    <span className="text-invert-navy font-medium leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Ideal for */}
      <Section id="ideal-for">
        <SectionHeader
          label="Who It's For"
          title="Built for local businesses"
          subtitle="If your customers live and shop nearby, INVERT puts your name in front of them."
        />
        <FadeIn>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {IDEAL_FOR.map((type) => (
              <span
                key={type}
                className="px-5 py-2.5 rounded-full border border-invert-border bg-white text-sm font-medium text-invert-navy hover:border-invert-orange/40 hover:text-invert-orange transition-colors"
              >
                {type}
              </span>
            ))}
          </div>
        </FadeIn>
      </Section>

      {/* How it works */}
      <Section id="how-it-works" className="bg-slate-50">
        <SectionHeader
          label="How It Works"
          title="From hello to on-screen in four steps"
          subtitle="No dashboards to learn. Our team does the heavy lifting."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((step, i) => (
            <FadeIn key={step.number} delay={i * 0.08}>
              <div className="relative rounded-2xl bg-white border border-invert-border p-7 h-full">
                <span className="text-4xl font-bold text-invert-orange/30">{step.number}</span>
                <h3 className="text-lg font-semibold text-invert-navy mt-3 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn className="text-center mt-12">
          <CtaButton target="inquiry">Get Started</CtaButton>
        </FadeIn>
      </Section>

      {/* Pricing */}
      <Section id="pricing">
        <SectionHeader
          label="Pricing"
          title="Start small. Scale as you grow."
          subtitle="Simple monthly packages with no long-term contract. Add screens whenever you're ready."
        />
        <FadeIn className="max-w-3xl mx-auto">
          <div className="rounded-3xl border border-invert-border bg-white overflow-hidden shadow-sm">
            <div className="grid md:grid-cols-2">
              <div className="bg-invert-navy text-white p-8 lg:p-10 flex flex-col justify-center">
                <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-3">
                  Starting at
                </p>
                <div className="flex items-end gap-1 mb-2">
                  <span className="text-5xl lg:text-6xl font-bold">${startingPrice}</span>
                  <span className="text-slate-300 mb-2">/mo per screen</span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Begin with a single screen in your neighborhood. Final pricing depends on the screens and locations
                  you choose — we&apos;ll put together a plan that fits your budget.
                </p>
              </div>
              <div className="p-8 lg:p-10">
                <p className="text-sm font-semibold text-invert-navy mb-4">Every package includes</p>
                <ul className="space-y-3 mb-8">
                  {PRICING_INCLUDES.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-invert-orange/15 text-invert-orange text-xs shrink-0 mt-0.5">
                        ✓
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => scrollToSection('inquiry')}
                  className="w-full inline-flex items-center justify-center px-6 py-4 rounded-xl text-base font-semibold text-white bg-invert-orange hover:bg-invert-orange-deep shadow-lg shadow-invert-orange/20 transition-colors"
                >
                  Request Your Pricing
                </button>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* Credibility / network proof */}
      <Section id="network" className="bg-slate-50">
        <SectionHeader
          label="The Network"
          title="Real screens. Real venues. Real foot traffic."
          subtitle="A live, growing network of screens in local businesses — these are the places your ad will play."
        />
        <ProofStatsBar className="mb-12" />
        <ProofPhotoWall />
        {filledLogos.length > 0 && (
          <FadeIn className="mt-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 text-center mb-6">
              Brands already advertising with INVERT
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {filledLogos.map((advertiser) => (
                <AdvertiserLogoTile key={advertiser.label} advertiser={advertiser} />
              ))}
            </div>
          </FadeIn>
        )}
      </Section>

      {/* FAQ */}
      <Section id="faq">
        <SectionHeader label="FAQ" title="Questions before you reach out?" />
        <div className="max-w-3xl mx-auto divide-y divide-invert-border">
          {FAQS.map((item, i) => (
            <FadeIn key={item.q} delay={i * 0.04}>
              <details className="group py-5">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-base font-semibold text-invert-navy pr-6">{item.q}</span>
                  <span className="text-invert-orange text-xl shrink-0 transition-transform duration-200 group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="text-sm text-slate-600 leading-relaxed mt-3">{item.a}</p>
              </details>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Final CTA + inquiry */}
      <section id="inquiry" className="relative bg-gradient-to-br from-invert-navy to-invert-navy-muted text-white py-20 lg:py-28 scroll-mt-20">
        <div className="absolute inset-0 bg-glow-orange opacity-50 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-4">
                Get Started
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-5">
                See if INVERT is a fit for your business
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Tell us a little about your business and we&apos;ll reply within one business day with screen options,
                neighborhoods, and pricing. No account, no commitment.
              </p>
              <ul className="space-y-3">
                {['A plan tailored to your area and budget', 'Clear, upfront pricing', 'A real reply within one business day'].map((point) => (
                  <li key={point} className="flex items-center gap-3 text-slate-200">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-invert-orange/20 text-invert-orange text-sm shrink-0">
                      ✓
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </FadeIn>
            <FadeIn delay={0.15}>
              <AdvertiserInquiryForm source={INQUIRY_SOURCE} />
            </FadeIn>
          </div>
        </div>
      </section>
    </AdvertiserLandingLayout>
  );
}
